import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Menu, Compass, MapPin, Anchor, Instagram, Youtube, MessageCircle, Navigation, Ship, BookOpen } from 'lucide-react';
import ArticleView from './components/ArticleView';
import NewsletterForm from './components/NewsletterForm';
import { TimelineMenu } from './components/TimelineMenu';
import { Breadcrumbs } from './components/Breadcrumbs';
import { Pagination } from './components/Pagination';
import { TagCloud } from './components/TagCloud';
import { ArticleTags } from './components/ArticleTags';
import { getArticles, getPopularTags, getArticleBySlug, getKnowledgeAreasWithArticles, getCategoryBySlug, type Article, type Tag, type Category, type KnowledgeAreaWithCount } from './lib/supabase';
import { HelmetProvider } from 'react-helmet-async';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { BUILD_VERSION } from './build-version';
import { ImageProvider } from './contexts/ImageContext';

// BUILD_VERSION é usado para forçar uma nova build
console.log('Build version:', BUILD_VERSION);

function App() {
  const navigate = useNavigate();
  const { slug, category } = useParams();
  const [showArticle, setShowArticle] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeAreaWithCount[]>([]);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const articlesPerPage = 9;

  // Efeito para lidar com a rota inicial
  useEffect(() => {
    const handleInitialRoute = async () => {
      if (!initialLoadDone) {
        setInitialLoadDone(true);
        
        if (slug) {
          try {
            setLoading(true);
            const article = await getArticleBySlug(slug);
            if (article) {
              setSelectedArticle(article);
              setShowArticle(true);
            }
          } catch (error) {
            console.error('Error loading article:', error);
            navigate('/');
          } finally {
            setLoading(false);
          }
        } else if (category) {
          setSelectedCategory(category);
        }
      }
    };

    handleInitialRoute();
  }, [slug, category, navigate, initialLoadDone]);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset da página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, debouncedSearchQuery, selectedTags]);

  // Carregamento de artigos, áreas de conhecimento, categoria atual e tags
useEffect(() => {
  const loadData = async () => {
    try {
      setLoading(true);

      const categoryData = selectedCategory
        ? await getCategoryBySlug(selectedCategory)
        : null;

      setCurrentCategory(categoryData);

      if (selectedCategory === 'conhecimento') {
        const [areasData, tagsData] = await Promise.all([
          getKnowledgeAreasWithArticles(),
          getPopularTags()
        ]);

        setKnowledgeAreas(areasData);
        setArticles([]);
        setTags(tagsData);
      } else {
        const [articlesData, tagsData] = await Promise.all([
          getArticles(selectedCategory, debouncedSearchQuery, selectedTags),
          getPopularTags()
        ]);

        setArticles(articlesData);
        setKnowledgeAreas([]);
        setTags(tagsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!slug) {
    loadData();
  }
}, [selectedCategory, debouncedSearchQuery, selectedTags, slug]);


  const categories = [
    { name: 'Home', icon: <Ship className="w-6 h-6" />, slug: 'home' },
    { name: 'Dicas', icon: <Compass className="w-6 h-6" />, slug: 'dicas' },
    { name: 'Lugares', icon: <MapPin className="w-6 h-6" />, slug: 'lugares' },
    { name: 'Tecnologia', icon: <Anchor className="w-6 h-6" />, slug: 'tecnologia' },
    { name: 'Conhecimento', icon: <BookOpen className="w-6 h-6" />, slug: 'conhecimento' },
  ];

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    setShowArticle(true);
    navigate(`/artigo/${article.slug}`);
  };

  const handleCategoryClick = (slug: string) => {
    if (slug === 'home') {
      setSelectedCategory(undefined);
      setShowArticle(false);
      setSelectedArticle(null);
      setSelectedTags([]);
      navigate('/');
    } else {
      setSelectedCategory(slug);
      setShowArticle(false);
      setSelectedArticle(null);
      setSelectedTags([]);
      navigate(`/categoria/${slug}`);
    }
  
    setIsMenuOpen(false);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(articles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const currentArticles = articles.slice(startIndex, endIndex);

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="flex-1">
          <nav className="bg-white shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex items-center">
                  <img 
                    src="/logo.png" 
                    alt="Blog Roda de Proa" 
                    className="h-10 w-auto cursor-pointer md:block hidden" 
                    onClick={() => {
                      setShowArticle(false);
                      setSelectedArticle(null);
                      setSelectedCategory(undefined);
                      setSelectedTags([]);
                      setIsMenuOpen(false);
                      navigate('/');
                    }}
                  />
                </div>
                <div className="hidden md:flex items-center space-x-8">
                  {categories.filter(cat => cat.slug !== 'home').map((category) => (
                    <button
                      key={category.slug}
                      onClick={() => handleCategoryClick(category.slug)}
                      className={`flex items-center space-x-1 ${
                        selectedCategory === category.slug
                          ? 'text-blue-600'
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {category.icon}
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar artigos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button 
                    className="md:hidden p-2 text-gray-600 hover:text-blue-600"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
                    aria-expanded={isMenuOpen}
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              {/* Menu móvel */}
              <div 
                className={`md:hidden transition-all duration-300 ease-in-out ${
                  isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
              >
                <div className="px-2 pt-2 pb-3 space-y-1">
                  {categories.map((category) => (
                    <button
                      key={category.slug}
                      onClick={() => {
                        handleCategoryClick(category.slug);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md ${
                        selectedCategory === category.slug
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {category.icon}
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {showArticle && selectedArticle ? (
            <ArticleView 
              article={selectedArticle} 
              onBack={() => {
                setShowArticle(false);
                navigate(-1);
              }}
              onTagClick={(tag) => {
                setShowArticle(false);
                setSelectedTags([tag]);
              }}
              onCategoryClick={handleCategoryClick}
              parentCategory={
                currentCategory?.parent_id
                  ? { name: 'Conhecimento', slug: 'conhecimento' }
                  : undefined
              }
            />
          ) : (
            <>
              <div className="relative bg-blue-600 text-white overflow-hidden">
                <div className="absolute inset-0">
                  <img
                    src="/banner_blog.webp"
                    alt="Banner do Blog"
                    className="w-full h-full object-cover opacity-70"
                  />
                  <div className="absolute inset-0 bg-blue-600 opacity-40"></div>
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                  <div className="max-w-3xl">
                    <h1 className="text-4xl font-bold mb-6">
                      Explorando o Mundo da Navegação
                    </h1>
                    <p className="text-xl mb-8">
                      Seu guia completo sobre navegação, destinos náuticos e inovações marítimas.
                    </p>
                    <NewsletterForm />
                  </div>
                </div>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="flex flex-col md:flex-row">
                  <div className={`flex-1 ${selectedCategory ? 'md:mr-8' : ''}`}>
                    <Breadcrumbs 
                      parentCategory={
                        currentCategory?.parent_id
                          ? { name: 'Conhecimento', slug: 'conhecimento' }
                          : undefined
                      }
                      category={currentCategory ?? undefined}
                      tags={selectedTags}
                      onTagRemove={(tag) => setSelectedTags(prev => prev.filter(t => t !== tag))}
                      onHomeClick={() => {
                        setShowArticle(false);
                        setSelectedArticle(null);
                        setSelectedCategory(undefined);
                        setSelectedTags([]);
                        navigate('/');
                      }}
                      onCategoryClick={handleCategoryClick}
                    />

                    {loading ? (
                      <div className="flex justify-center items-center min-h-[400px]">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                      </div>
                   ) : selectedCategory === 'conhecimento' ? (
  <div>
    {knowledgeAreas.length === 0 ? (
      <div className="text-center py-12">
        <p className="text-gray-600">
          Nenhum conteúdo disponível no momento.
        </p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {knowledgeAreas.map((area) => (
          <button
          key={area.id}
          type="button"
          onClick={() => handleCategoryClick(area.slug)}
          className="bg-white border border-green-700 rounded-lg p-6 text-center hover:shadow-md transition-shadow duration-300"
        >
          <h2 className="text-xl font-semibold text-gray-900">
            {area.name}
          </h2>
        
          <p className="mt-2 text-sm text-gray-600">
            {area.article_count} {area.article_count === 1 ? 'artigo' : 'artigos'}
          </p>
        </button>
        ))}
      </div>
    )}
  </div>
) : articles.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-600">Nenhum artigo encontrado.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
                        {currentArticles.map((article) => (
                          <article
                            key={article.id}
                            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                            itemScope
                            itemType="http://schema.org/Article"
                            onClick={() => handleArticleClick(article)}
                          >
                            <div className="block">
                              {article.featured_image && (
                                <div className="relative h-48">
                                  <img
                                    src={article.featured_image}
                                    alt={article.title}
                                    className="w-full h-full object-cover"
                                    itemProp="image"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <div className="p-6">
                                <h2
                                  className="text-xl font-bold mb-2 text-gray-900 hover:text-blue-600 transition-colors duration-200"
                                  itemProp="headline"
                                >
                                  {article.title}
                                </h2>
                                <p className="text-gray-600 mb-4 line-clamp-2" itemProp="description">
                                  {article.excerpt}
                                </p>
                                <div className="flex items-center justify-between text-sm text-gray-500">
                                  <span itemProp="datePublished">
                                    {new Date(article.published_at).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  <div className="flex items-center">
                                    <span className="mr-2">{article.likes_count || 0}</span>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className={`h-5 w-5 ${
                                        article.user_has_liked ? 'text-red-500 fill-current' : 'text-gray-400'
                                      }`}
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}

                    {!loading && articles.length > articlesPerPage && (
                      <div className="mt-12">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>

                  {selectedCategory && (
                    <div className="hidden lg:block w-64">
                      <TimelineMenu
                        articles={articles}
                        onArticleClick={handleArticleClick}
                      />
                    </div>
                  )}
                </div>

                {tags.length > 0 && (
                  <div className="mt-16">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6">Tags Populares</h2>
                    <TagCloud
                      tags={tags}
                      selectedTags={selectedTags}
                      onTagClick={handleTagClick}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    </HelmetProvider>
  );
}

export default function AppWrapper() {
  return (
    <ImageProvider>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ImageProvider>
  );
}